import { useState } from 'react'
import { Search, Store, Shield, Users, ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'

const mockBrands = [
  { id: '1', name: 'NeonLabs', logo: '🎮', description: '赛博朋克风格潮玩', followers: 12500, products: 24, verified: true },
  { id: '2', name: '星绘社', logo: '🎨', description: '数字艺术藏品', followers: 8900, products: 18, verified: true },
  { id: '3', name: 'PhygitalX', logo: '⚡', description: '虚实联动潮玩', followers: 15200, products: 32, verified: true },
  { id: '4', name: '潮玩工房', logo: '📦', description: '限量潮玩工作室', followers: 6700, products: 12, verified: false },
]

const featuredProducts = [
  { id: 1, name: '赛博潮玩 #001', brand: 'NeonLabs', price: 299, image: '/nft1.jpg', verified: true },
  { id: 2, name: '限量手办·夜光版 #042', brand: '星绘社', price: 159, image: '/nft2.jpg', verified: true },
  { id: 3, name: '虚实联动 #088', brand: 'PhygitalX', price: 599, image: '/nft3.jpg', verified: true },
  { id: 4, name: '限量徽章·隐藏款 #007', brand: '潮玩工房', price: 99, image: '/nft4.jpg', verified: false },
]

export default function Home() {
  const [searchKeyword, setSearchKeyword] = useState('')
  const [activeTab, setActiveTab] = useState('brands')

  const filteredBrands = mockBrands.filter(b =>
    b.name.toLowerCase().includes(searchKeyword.toLowerCase())
  )

  return (
    <div className="space-y-6 py-4">
      {/* Hero */}
      <div className="cyber-card rounded-2xl p-6 corner-accent">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-cyber-neon" />
          <span className="text-cyber-neon font-cyber text-sm tracking-wider">正品认证 · 担保交易</span>
        </div>
        <p className="text-gray-400 text-sm mb-4">
          源头认证让正品可被证明，担保交易让粉丝敢付款
        </p>
        <Link
          to="/verify"
          className="cyber-btn inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium"
        >
          <Search className="w-4 h-4" />
          立即验真
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { icon: Shield, label: '已验证', value: '12.5K', color: 'text-cyber-yellow' },
          { icon: Store, label: '品牌方', value: '156', color: 'text-cyber-neon' },
          { icon: Users, label: '用户', value: '8.2K', color: 'text-cyber-pink' },
        ].map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className="cyber-card rounded-xl p-3 text-center">
              <Icon className={`w-5 h-5 ${stat.color} mx-auto mb-1`} />
              <div className={`text-lg font-cyber font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-[10px] text-gray-500">{stat.label}</div>
            </div>
          )
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
        <input
          type="text"
          placeholder="搜索品牌或商品..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-cyber-dark border border-cyber-border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-cyber-neon"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'brands', label: '品牌' },
          { id: 'products', label: '商品' },
          { id: 'verified', label: '已认证' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-cyber-neon/20 text-cyber-neon border border-cyber-neon/50'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Brand Grid */}
      {activeTab === 'brands' && (
        <div className="grid grid-cols-2 gap-3">
          {filteredBrands.map((brand) => (
            <Link
              key={brand.id}
              to={`/shop/${brand.id}`}
              className="cyber-card rounded-xl p-4 hover:shadow-lg hover:shadow-cyber-neon/10 transition-all"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyber-neon/20 to-cyber-pink/20 border border-cyber-border flex items-center justify-center text-2xl">
                  {brand.logo}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-white truncate">{brand.name}</span>
                    {brand.verified && (
                      <Shield className="w-3 h-3 text-cyber-neon shrink-0" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500 truncate">{brand.description}</div>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{brand.followers.toLocaleString()} 粉丝</span>
                <span>{brand.products} 商品</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Product Grid */}
      {activeTab === 'products' && (
        <div className="grid grid-cols-2 gap-3">
          {featuredProducts.map((product) => (
            <div key={product.id} className="cyber-card rounded-xl overflow-hidden group cursor-pointer">
              <div className="aspect-square bg-cyber-dark relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-lg bg-cyber-panel border border-cyber-border flex items-center justify-center">
                    <span className="text-2xl">🎴</span>
                  </div>
                </div>
                {product.verified && (
                  <div className="absolute top-2 right-2 px-2 py-1 rounded-md text-[10px] font-bold bg-gradient-to-r from-cyber-neon to-cyber-pink text-white">
                    已认证
                  </div>
                )}
              </div>
              <div className="p-3">
                <div className="text-xs text-cyber-neon mb-1">{product.brand}</div>
                <div className="text-sm font-medium truncate">{product.name}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-cyber-yellow font-cyber font-bold">¥{product.price}</span>
                  <button className="w-8 h-8 rounded-lg bg-cyber-neon/20 border border-cyber-neon/50 flex items-center justify-center text-cyber-neon hover:bg-cyber-neon/30 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
